import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ArrowRight, AlertCircle } from "lucide-react";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function googleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplit>
      <div className="max-w-md w-full">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] mb-3">
          Sign in
        </div>
        <h1 className="font-heading font-bold tracking-tight text-4xl text-brand leading-none">
          Welcome back.
        </h1>
        <p className="text-[#5C5C59] mt-3">
          Sign in to access policy-grade infrastructure intelligence.
        </p>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="google-signin-btn"
          className="w-full mt-8 flex items-center justify-center gap-3 border border-brand-border bg-white text-[#111110] rounded-md px-4 py-3 text-sm font-medium hover:bg-brand-bg transition-colors"
        >
          <GoogleIcon /> Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs text-[#5C5C59] font-mono uppercase tracking-wider">
          <div className="flex-1 h-px bg-brand-border" />
          or
          <div className="flex-1 h-px bg-brand-border" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} testid="email-input" required />
          <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} testid="password-input" required />
          {error && (
            <div className="flex items-start gap-2 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3" data-testid="signin-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="signin-submit"
            className="w-full bg-brand text-white rounded-md px-4 py-3 text-sm font-medium hover:bg-brand-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/forgot-password" className="text-[#5C5C59] hover:text-brand">Forgot password?</Link>
          <Link to="/signup" className="text-brand hover:text-brand-deep font-medium" data-testid="goto-signup">Create account</Link>
        </div>
      </div>
    </AuthSplit>
  );
}

export function Field({ label, type, value, onChange, testid, autoComplete, required }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C59]">{label}</span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-brand-border rounded-md px-4 py-3 text-sm text-[#111110] focus:outline-none focus:border-brand transition-colors"
      />
    </label>
  );
}

export function AuthSplit({ children }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-brand-bg">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,54,34,0.55), rgba(10,54,34,0.85)), url(https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1600)",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center" data-testid="auth-brand-home">
            <img src="/brand/logo-light-full.png" alt="Infralytics-GIIS" className="h-14 w-auto object-contain" />
          </Link>
          <div className="max-w-md">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mb-3">Connectivity Impact Forecaster</div>
            <h2 className="font-heading font-bold text-3xl leading-tight">
              See the risk. See the opportunity.
              <br />
              <span className="text-brand-accent">Make the investment case.</span>
            </h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              Data-driven digital investment decisions for Local and Combined Authorities.
              Quantify what earlier initiatives actually delivered, and build the case for the next
              one — postcode-level, board-ready, Green Book aligned.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">{children}</div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.5 12.25c0-.7-.06-1.4-.18-2.07H12v3.92h5.92a5.06 5.06 0 0 1-2.2 3.32v2.75h3.55c2.08-1.92 3.28-4.74 3.28-7.92z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.75c-.98.66-2.24 1.05-3.73 1.05-2.87 0-5.3-1.94-6.17-4.55H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853"/>
      <path d="M5.83 14.09a6.6 6.6 0 0 1-.34-2.09c0-.73.12-1.43.34-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.65-2.84z" fill="#FBBC05"/>
      <path d="M12 5.36c1.62 0 3.07.56 4.21 1.65l3.16-3.16C17.45 2.05 14.96 1 12 1A11 11 0 0 0 2.18 7.07l3.65 2.84C6.7 7.3 9.13 5.36 12 5.36z" fill="#EA4335"/>
    </svg>
  );
}
